import { GQLGetPlansPricingQuery } from '@upheal/graphql/definitions/billing/GetPlansPricing.generated';
import {
    GQLPlansPricing,
    GQLSpend,
    GQLTimeBasedPlanPricing,
} from '@upheal/graphql/definitions/schema.generated';
import { isPlan, PricingTier } from './plans';

export const formatPriceWithCurrency = (
    price: number,
    currency: string,
    skipCurrencyLetters = false,
) => {
    const fractionDigits = price % 1 !== 0 ? 2 : 0;
    const locale = ['CAD', 'AUD', 'NZD'].includes(currency.toUpperCase())
        ? 'en-US'
        : navigator.language;

    let formattedPrice = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    }).format(price);

    if (
        skipCurrencyLetters &&
        ['USD', 'CAD', 'AUD', 'NZD', 'GBP', 'JPY'].includes(
            currency.toUpperCase(),
        )
    ) {
        formattedPrice = formattedPrice.replace(/[^\d.,$€£¥₹]/g, '');
    }

    return formattedPrice;
};

export const FAILED_PAYMENT_UPDATE_WINDOW_DAYS = 5;

export const formatSpend = (spend: GQLSpend, currency: string) => {
    const spentAmount = spend.amount / 100;
    return formatPriceWithCurrency(spentAmount, currency);
};

const DEFAULT_SAVINGS_PERCENTAGE = 20;

export const calculateMinSavingsPercentageForAvailablePlans = (
    pricingData: GQLPlansPricing | null | undefined,
): number => {
    if (!pricingData || pricingData.timeBasedPlansPricing.length === 0) {
        return DEFAULT_SAVINGS_PERCENTAGE;
    }

    const validSavings = pricingData.timeBasedPlansPricing
        .map((plan: GQLTimeBasedPlanPricing) =>
            calculateSavingsPercentageForPlan(plan),
        )
        .filter((saving) => !isNaN(saving)); // Filter out NaN values (e.g., from division by zero)

    if (validSavings.length === 0) {
        return DEFAULT_SAVINGS_PERCENTAGE;
    }

    return Math.floor(Math.min(...validSavings));
};

export const calculateSavingsPercentageForPlan = (
    plan: GQLTimeBasedPlanPricing,
) => {
    if (plan.monthlyPrice === 0) {
        return NaN; // Mark as invalid for calculation if price is zero
    }
    return Math.floor(
        ((plan.monthlyPrice - plan.monthlyPriceYearlyDiscount) /
            plan.monthlyPrice) *
            100,
    );
};

type PricingData = GQLGetPlansPricingQuery['getPlansPricing'];

export const getLowestPriceForTier = (
    pricingData: PricingData | undefined,
    tier: PricingTier,
) => {
    const plan = pricingData?.timeBasedPlansPricing.find(({ planId }) =>
        isPlan(planId, tier),
    );

    return plan?.monthlyPriceYearlyDiscount;
};

export const calculateYearlyDiscount = (
    price: number,
    priceWithYearlyDiscount: number,
) => {
    return (price - priceWithYearlyDiscount) * 12;
};
